import { Component, Input, inject } from '@angular/core';
import { BaseCommonDeliverable, MarkDeliverableFiles } from '@aiwyn/common/client-requests';
import { catchError, finalize, map, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { BehaviorSubject, combineLatest, defer, EMPTY } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { DATA_SELECTOR_INJECTION_TOKEN, DmsIntegrationApiService, downloadFileFromUrl, ignoreNullish, Logger } from '@aiwyn/shared';
import { BaseFile, ClientRequestApiService, MarkDeliverableFilesActions } from '@aiwyn/api';
import { Store } from '@ngxs/store';
import { ClearFocusedDeliverableId, ClientRequestsState } from '../../state';
import { FilePreviewService } from '@aiwyn/services';
import { ConfigService, LaunchDarklyFlag, LaunchDarklyService } from '@aiwyn/config';

@Component({
  selector: 'app-deliverable',
  templateUrl: './deliverable.component.html',
  styleUrls: ['./deliverable.component.scss'],
  providers: [
    {
      provide: DATA_SELECTOR_INJECTION_TOKEN,
      useValue: 'deliverable',
    },
  ],
  standalone: false,
})
export class DeliverableComponent {
  @Input() instance!: BaseCommonDeliverable;
  @Input() isGroupComplete!: boolean;
  public dataSelector = inject(DATA_SELECTOR_INJECTION_TOKEN);

  fileCount$ = defer(() => this.instance.files$).pipe(
    ignoreNullish(),
    map((files) => files.length),
    shareReplay(1),
  );

  focused$ = combineLatest([
    this._store.select(ClientRequestsState.getSlices.focusedDeliverableId),
    defer(() => this.instance.data$).pipe(map(({ id }) => id)),
  ]).pipe(
    take(1),
    map(([focusedDeliverableId, id]) => focusedDeliverableId === id),
    tap((focused) => {
      if (focused) {
        this._store.dispatch(new ClearFocusedDeliverableId());
      }
    }),
  );

  isSafeSendEnabled$ = defer(() => this.instance.isSafeSendDeliverable$).pipe(
    map((isSafeSendDeliverable) => this._configService.enableSafesendDeliverables && isSafeSendDeliverable),
    shareReplay(1),
  );

  isMultiContactsEnabled$ = this._launchDarklyService.getFeatureFlag(LaunchDarklyFlag.ENABLE_MULTI_CONTACTS_DELIVERABLES).pipe(
    shareReplay(1),
  );

  constructor(
    private readonly _dmsIntegrationApiService: DmsIntegrationApiService,
    private readonly _filePreviewService: FilePreviewService,
    private readonly _logger: Logger,
    private readonly _store: Store,
    private readonly _httpClient: HttpClient,
    private readonly _clientRequestApiService: ClientRequestApiService,
    private readonly _configService: ConfigService,
    private readonly _launchDarklyService: LaunchDarklyService,
  ) {}

  isDownloadAllFilesProcessing$ = new BehaviorSubject<boolean>(false);

  downloadAllFiles(): void {
    this.isDownloadAllFilesProcessing$.next(true);

    // Mark files as downloaded and then download them
    // This combines both operations into a single observable chain
    combineLatest([this.instance.id$, this.instance.files$])
      .pipe(
        take(1),
        // First mark files as downloaded
        switchMap(([deliverableId, files]) => {
          const fileIds = files.map((file) => file.id);
          return this._store.dispatch(new MarkDeliverableFiles(deliverableId, fileIds, MarkDeliverableFilesActions.DOWNLOADED)).pipe(
            // After marking is complete, return the files for downloading
            map(() => files),
          );
        }),
        // Finally download the files
        switchMap((files) => {
          return this._dmsIntegrationApiService
            .getDownloadUrl({
              files: files.map((file) => file.id),
              isMultipleDownload: true,
            })
            .pipe(
              tap((url) => {
                downloadFileFromUrl({
                  href: url,
                  download: files.map((file) => file.name).join(', '),
                });
              }),
            );
        }),
        // Catch errors that might occur in any part of the chain
        catchError((error) => {
          this._logger.error('Error in download process:', error);
          return EMPTY; // Complete the observable
        }),
        // Make sure isDownloadAllFilesProcessing is set to false when complete
        finalize(() => {
          this.isDownloadAllFilesProcessing$.next(false);
        }),
      )
      .subscribe();
  }

  previewFile(file: BaseFile): void {
    this.instance.id$
      .pipe(
        take(1),
        switchMap((deliverableId) =>
          this._store.dispatch(new MarkDeliverableFiles(deliverableId, [file.id], MarkDeliverableFilesActions.VIEWED)),
        ),
      )
      .subscribe();

    this._filePreviewService.preview({
      id: file.id,
      name: file.name,
      dmsFileId: file.dmsId ?? file.dmsFileId,
    });
  }

  fileIdsDownloading$ = new BehaviorSubject<Record<string, boolean>>({});

  downloadFile(file: BaseFile): void {
    this.fileIdsDownloading$.next({
      ...this.fileIdsDownloading$.value,
      [file.id]: true,
    });

    this.instance.id$
      .pipe(
        take(1),
        switchMap((deliverableId) =>
          this._store.dispatch(new MarkDeliverableFiles(deliverableId, [file.id], MarkDeliverableFilesActions.DOWNLOADED)),
        ),
      )
      .subscribe();

    this._dmsIntegrationApiService
      .getDownloadUrl({ files: [file.id], isMultipleDownload: false })
      .pipe(
        tap((url) => {
          downloadFileFromUrl({ href: url, download: file.name });
        }),
        finalize(() => {
          this.fileIdsDownloading$.next({
            ...this.fileIdsDownloading$.value,
            [file.id]: false,
          });
        }),
      )
      .subscribe();
  }

  isSafeSendViewLoading$ = new BehaviorSubject<boolean>(false);

  viewSafeSendDeliverable(): void {
    this.isSafeSendViewLoading$.next(true);

    this.instance.id$
      .pipe(
        take(1),
        switchMap((deliverableId) => this._clientRequestApiService.generateSafeSendDeepLink(deliverableId)),
        tap((url) => {
          if (url) {
            window.open(url, '_blank');
          }
        }),
        catchError((error) => {
          this._logger.error('Error generating SafeSend deep link:', error);
          return EMPTY;
        }),
        finalize(() => {
          this.isSafeSendViewLoading$.next(false);
        }),
      )
      .subscribe();
  }
}
